// frontend/src/components/team/WorkflowCard.tsx
import ArchiveIcon from '@mui/icons-material/ArchiveOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import HistoryIcon from '@mui/icons-material/HistoryOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrowOutlined';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import React, { useState } from 'react';
import type { Workflow } from '../../services/api';

const TRIGGER_COLORS = {
  cron: 'primary',
  manual: 'default',
  event: 'secondary',
} as const;

const INTEGRATION_LABELS: Record<string, string> = {
  jira: 'JIRA',
  github: 'GitHub',
  gchat: 'GChat',
  confluence: 'Confluence',
};

interface Props {
  workflow: Workflow;
  onToggleActive: (id: string, active: boolean) => void;
  onRunNow: (id: string) => void;
  onEdit: (workflow: Workflow) => void;
  onHistory: (workflow: Workflow) => void;
  onDelete: (id: string) => void;
  onArchive: (id: string) => void;
  isRunning?: boolean;
}

const WorkflowCard: React.FC<Props> = ({
  workflow,
  onToggleActive,
  onRunNow,
  onEdit,
  onHistory,
  onDelete,
  onArchive,
  isRunning = false,
}) => {
  const [confirmAction, setConfirmAction] = useState<'archive' | 'delete' | null>(null);

  const lastRun = workflow.last_run_at
    ? formatDistanceToNow(new Date(workflow.last_run_at), { addSuffix: true })
    : 'Never run';

  const scheduleLabel =
    workflow.trigger_type === 'cron'
      ? workflow.cron_expression ?? 'cron'
      : workflow.trigger_type === 'event'
      ? workflow.event_type ?? 'event'
      : 'on demand';

  const handleConfirm = () => {
    if (confirmAction === 'delete') {
      onDelete(workflow.id);
    } else if (confirmAction === 'archive') {
      onArchive(workflow.id);
    }
    setConfirmAction(null);
  };

  return (
    <Card variant="outlined" sx={{ mb: 1.5 }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Box flex={1} minWidth={0}>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="subtitle2" fontWeight={600} noWrap>
                {workflow.name}
              </Typography>
              <Chip
                label={workflow.trigger_type.toUpperCase()}
                size="small"
                color={TRIGGER_COLORS[workflow.trigger_type]}
                sx={{ fontWeight: 600, fontSize: '0.65rem' }}
              />
              <Typography variant="caption" color="text.secondary" noWrap>
                {scheduleLabel}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.75} mt={0.5} flexWrap="wrap">
              {workflow.enabled_integrations.map((k) => (
                <Chip
                  key={k}
                  label={INTEGRATION_LABELS[k] ?? k}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.65rem', height: 20 }}
                />
              ))}
              <Typography variant="caption" color="text.secondary">
                · Last run: {lastRun}
              </Typography>
            </Box>
          </Box>

          <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
            <Tooltip title="Run now">
              <span>
                <IconButton
                  size="small"
                  onClick={() => onRunNow(workflow.id)}
                  disabled={isRunning}
                >
                  <PlayArrowIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => onEdit(workflow)}>
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Run history">
              <IconButton size="small" onClick={() => onHistory(workflow)}>
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Archive">
              <IconButton size="small" onClick={() => setConfirmAction('archive')} color="warning">
                <ArchiveIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" onClick={() => setConfirmAction('delete')} color="error">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Switch
              size="small"
              checked={workflow.is_active}
              onChange={(e) => onToggleActive(workflow.id, e.target.checked)}
            />
          </Box>
        </Box>
      </CardContent>

      <Dialog
        open={confirmAction !== null}
        onClose={() => setConfirmAction(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {confirmAction === 'delete' ? 'Delete workflow?' : 'Archive workflow?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmAction === 'delete'
              ? 'This will permanently delete the workflow and all its run history. This cannot be undone.'
              : 'This will hide the workflow from your list and stop any scheduled runs. You can still find it later.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmAction(null)}>Cancel</Button>
          <Button
            onClick={handleConfirm}
            color={confirmAction === 'delete' ? 'error' : 'warning'}
            variant="contained"
          >
            {confirmAction === 'delete' ? 'Delete' : 'Archive'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default WorkflowCard;
